/**
 * FAQ Agent for Ameen Insurance Assistant
 * 
 * Handles insurance questions using FAQ knowledge base.
 * 
 * TOOLS (EXACT names from backend):
 * - search_faq: Search FAQ knowledge base (MUST call before answering)
 * - go_to_policy_agent: Switch to policy flow
 * - go_to_claim_agent: Switch to claim flow
 * - go_to_router_agent: Return to menu
 */

import { AgentNode, AgentContext, ToolCallResult, VoiceGender, UIStateManager } from '../types';
import { noEntryRequirements } from '../guardrails';
import { logger } from '../../utils/logger';
import { handleSearchFAQ } from './RouterAgent';
import { vectorFAQService } from '../../services/vectorFAQService';

/**
 * Create FAQ Agent node
 */
export function createFAQAgent(voiceGender: VoiceGender): AgentNode {
  return {
    id: 'faq_agent',
    name: 'FAQ Agent',
    description: 'Answers insurance questions using FAQ knowledge base',
    
    entryGuardrails: noEntryRequirements(),
    
    exitGuardrails: {
      transitions: new Map([
        ['go_to_policy', {
          target: { agentId: 'policy_agent' },
          guardrails: []
        }],
        ['go_to_claim', {
          target: { agentId: 'claim_agent' },
          guardrails: []
        }],
        ['go_to_router', {
          target: { agentId: 'router_agent' },
          guardrails: []
        }]
      ]),
      fallback: { agentId: 'router_agent' }
    },
    
    initialPrompt: 'أسئلة شائعة.',
    
    /**
     * Handle tool calls - SINGLE SOURCE OF TRUTH for FAQ agent tools
     */
    handleToolCall: async (
      toolName: string,
      args: Record<string, any>,
      context: AgentContext,
      uiState?: UIStateManager
    ): Promise<ToolCallResult> => {
      logger.tool('[FAQAgent] Tool called:', toolName, args);
      
      switch (toolName) {
        // ═══ ROUTING TOOLS ═══
        case 'go_to_router_agent':
          return {
            toolName,
            args,
            result: { success: true, message: 'Returning to menu' },
            triggersTransition: true,
            transitionTo: 'router_agent',
            transitionData: { return_to_menu: true },
          };
        
        case 'go_to_policy_agent':
          return {
            toolName,
            args,
            result: { success: true, message: 'Switching to policy flow' },
            triggersTransition: true,
            transitionTo: 'policy_agent'
          };
        
        case 'go_to_claim_agent':
          return {
            toolName,
            args,
            result: { success: true, message: 'Switching to claim flow' },
            triggersTransition: true,
            transitionTo: 'claim_agent'
          };
        
        // ═══ FAQ TOOL ═══
        case 'search_faq':
          return await handleSearchFAQ(args, toolName);
        
        default:
          logger.warn('[FAQAgent] Unknown tool:', toolName);
          return {
            toolName,
            args,
            result: { success: false, error: `Unknown tool: ${toolName}` }
          };
      }
    },
    
    onActivate: async (context: AgentContext) => {
      logger.info('[FAQAgent] Activated');
      
      // Ensure FAQ service is initialized
      try {
        await vectorFAQService.initialize();
        logger.info('[FAQAgent] FAQ service ready');
      } catch (e) {
        logger.error('[FAQAgent] Failed to initialize FAQ service:', e);
      }
    },
    
    onDeactivate: async (context: AgentContext) => {
      logger.info('[FAQAgent] Deactivated');
    }
  };
}
